import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";

// The partner's current view lives in the URL, so a view can be bookmarked
// or pasted to another partner, and the last view is remembered per browser.
// Shape: { start, end, practiceAreas: [one area], attorneys: [], includeArchived }
//
// Practice area is the dashboard's top-level category (Matthew, 2026-10-16:
// "no firm-wide numbers anywhere"), so exactly one is always selected. The
// array shape is kept because the API takes a list.

const STORAGE_KEY = "partners.dashboard.lastView";
export const DEFAULT_PRACTICE_AREA = "VA Agency";
const oneArea = (list) => [(list && list[0]) || DEFAULT_PRACTICE_AREA];

export const PRESETS = [
  { key: "mtd", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "qtd", label: "This quarter" },
  { key: "last_90", label: "Last 90 days" },
  { key: "ytd", label: "Year to date" },
  { key: "last_12", label: "Last 12 months" },
];

export const presetRange = (key, today = dayjs()) => {
  const t = today.startOf("day");
  switch (key) {
    case "last_month": {
      const s = t.subtract(1, "month").startOf("month");
      return [s, s.endOf("month")];
    }
    case "qtd": {
      const qStartMonth = Math.floor(t.month() / 3) * 3;
      return [t.month(qStartMonth).startOf("month"), t];
    }
    case "last_90":
      return [t.subtract(89, "day"), t];
    case "ytd":
      return [t.startOf("year"), t];
    case "last_12":
      return [t.subtract(11, "month").startOf("month"), t];
    case "mtd":
    default:
      return [t.startOf("month"), t];
  }
};

export const matchPreset = (start, end, today = dayjs()) => {
  for (const { key } of PRESETS) {
    const [s, e] = presetRange(key, today);
    if (s.format("YYYY-MM-DD") === start && e.format("YYYY-MM-DD") === end) return key;
  }
  return null;
};

const splitList = (value) => (value ? value.split(",").map((v) => v.trim()).filter(Boolean) : []);

const readStored = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeStored = (filters) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch (e) {
    /* private mode etc. — remembering the view is a nicety, not a need */
  }
};

export default function usePartnerFilters() {
  const [params, setParams] = useSearchParams();

  const filters = useMemo(() => {
    const hasAny = ["start", "end", "pa", "atty", "arch"].some((k) => params.has(k));
    const stored = hasAny ? null : readStored();
    const [defStart, defEnd] = presetRange("mtd");
    return {
      start: params.get("start") || stored?.start || defStart.format("YYYY-MM-DD"),
      end: params.get("end") || stored?.end || defEnd.format("YYYY-MM-DD"),
      practiceAreas: oneArea(params.has("pa") ? splitList(params.get("pa")) : stored?.practiceAreas),
      attorneys: params.has("atty") ? splitList(params.get("atty")) : stored?.attorneys || [],
      includeArchived: true,
    };
  }, [params]);

  useEffect(() => {
    writeStored(filters);
  }, [filters]);

  const update = useCallback(
    (patch) => {
      const next = { ...filters, ...patch };
      const out = new URLSearchParams();
      out.set("start", next.start);
      out.set("end", next.end);
      out.set("pa", oneArea(next.practiceAreas).join(","));
      out.set("atty", (next.attorneys || []).join(","));
      setParams(out, { replace: true });
    },
    [filters, setParams]
  );

  const setPracticeArea = useCallback((area) => update({ practiceAreas: [area] }), [update]);

  const toggleAttorney = useCallback(
    (id) => {
      const current = filters.attorneys || [];
      const next = current.includes(id) ? current.filter((a) => a !== id) : [...current, id];
      update({ attorneys: next });
    },
    [filters.attorneys, update]
  );

  const applyPreset = useCallback(
    (key) => {
      const [s, e] = presetRange(key);
      update({ start: s.format("YYYY-MM-DD"), end: e.format("YYYY-MM-DD") });
    },
    [update]
  );

  const clear = useCallback(() => {
    const [s, e] = presetRange("mtd");
    update({ start: s.format("YYYY-MM-DD"), end: e.format("YYYY-MM-DD"), attorneys: [], includeArchived: true });
  }, [update]);

  return { filters, practiceArea: filters.practiceAreas[0], update, setPracticeArea, toggleAttorney, applyPreset, clear };
}
